// The layout maths behind the dashboard grid, kept apart from the DOM so it can be tested
// without a browser. Tiles are `{ id, x, y, w, h }` on a 24-column grid; resizing one pushes
// whatever it now overlaps downwards, then everything floats back up into the gaps.
//
// This is why a resize feels like the tile "pins to its new position" rather than the page
// reflowing by whatever order the tiles happen to sit in the DOM.
export const COLS = 24;
export const MIN_W = 6;
export const MIN_H = 6;

export const collides = (a, b) =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

// Anything the moved tile now overlaps drops below it, and anything that overlap displaces
// drops in turn. The guards bound the work: a cycle would otherwise be an infinite loop on a
// page the person is actively dragging.
export function resolve(layout, moved) {
  const others = layout.filter((l) => l !== moved).sort((a, b) => a.y - b.y || a.x - b.x);
  for (const item of others) {
    let guard = 0;
    while (collides(item, moved) && guard++ < 60) item.y = moved.y + moved.h;
    for (const other of others) {
      if (other === item) continue;
      let inner = 0;
      while (collides(item, other) && item.y >= other.y && inner++ < 60) item.y = other.y + other.h;
    }
  }
  return layout;
}

// Float every tile up into the space above it. Without this a tile dragged shorter leaves a
// hole, and the dashboard slowly turns into a column of islands.
export function compact(layout, pinned = null) {
  const sorted = [...layout].sort((a, b) => a.y - b.y || a.x - b.x);
  for (const item of sorted) {
    if (item === pinned) continue;
    while (item.y > 0) {
      const probe = { ...item, y: item.y - 1 };
      if (sorted.some((o) => o !== item && collides(probe, o))) break;
      item.y -= 1;
    }
  }
  return layout;
}

// Apply a new size (and optionally a new left edge, for the bottom-left handle), clamped to the
// grid, then settle everything around it.
export function resize(layout, id, { w, h, x }) {
  const item = layout.find((l) => l.id === id);
  if (!item) return { layout, changed: false };
  const nextW = Math.max(MIN_W, Math.min(COLS, Math.round(w)));
  const nextH = Math.max(MIN_H, Math.round(h));
  const nextX = Math.max(0, Math.min(COLS - nextW, Math.round(x ?? item.x)));
  if (nextW === item.w && nextH === item.h && nextX === item.x) return { layout, changed: false };
  item.w = nextW;
  item.h = nextH;
  item.x = nextX;
  resolve(layout, item);
  compact(layout, item);
  return { layout, changed: true };
}

// Push tiles down until nothing overlaps anything, in reading order. Used when a layout
// arrives from outside — storage on another device, an older version of this page — where two
// tiles may sit on the same square. Overlapping tiles hide one another, which looks exactly
// like a tile that has gone missing.
export function settle(layout) {
  const ordered = [...layout].sort((a, b) => a.y - b.y || a.x - b.x);
  const placed = [];
  for (const item of ordered) {
    let guard = 0;
    while (placed.some((other) => collides(item, other)) && guard++ < 200) item.y += 1;
    placed.push(item);
  }
  return layout;
}

// A saved layout is only trusted as far as it parses: ids that no longer exist are dropped, and
// tiles the saved layout has never heard of keep the positions the server gave them. A stored
// layout from an older version of the page must never be able to hide a tile.
export function merge(defaults, saved) {
  if (!Array.isArray(saved)) return defaults.map((d) => ({ ...d }));
  const by = new Map(saved.filter((s) => s && typeof s.id === 'string').map((s) => [s.id, s]));
  const merged = defaults.map((d) => {
    const s = by.get(d.id);
    if (!s) return { ...d };
    const w = Number(s.w);
    const h = Number(s.h);
    const x = Number(s.x);
    const y = Number(s.y);
    if (![w, h, x, y].every((n) => Number.isFinite(n) && n >= 0)) return { ...d };
    return {
      ...d,
      w: Math.max(MIN_W, Math.min(COLS, Math.round(w))),
      h: Math.max(MIN_H, Math.round(h)),
      x: Math.max(0, Math.min(COLS - MIN_W, Math.round(x))),
      y: Math.round(y),
    };
  });
  return compact(settle(merged));
}
